// ⚠️ FICHIER ENGENDRÉ — NE PAS ÉDITER À LA MAIN.
// Source : lib/operateur-leviers.js + lib/leviers/*.js
// Régénérer : node scripts/generer-miroir-leviers.js
// Pourquoi ce doublon : CRA/webpack refuse les imports hors de src/ (Jest, lui,
// les accepte — d'où le risque de tests verts sur un build cassé).
// lib/leviers/pricing.js — LES LEVIERS DU PRIX, DE LA DISPO ET DE LA TAXE.
//
// ══ POURQUOI CETTE FAMILLE EST À PART ══════════════════════════════════════
//
// Dimitri, 2026-09-09 : « si je lui dis "change le prix de Cocon Naturel à
// 40 €", il n'a pas à deviner quand le faire. Je viens de le dire. Il prépare,
// je vois ce qu'il va faire, je clique. » C'est cette phrase qui a ouvert la
// famille — et c'est `pricing_set_manual` qui la porte de bout en bout
// (logement + dates + prix).
//
// ⚠️ MAIS C'EST LE CHEMIN ARGENT, et il a deux propriétés qu'aucune autre
// famille n'a :
//
//   1. UN PUSH SORT DE CHEZ FBZ. Beds24 rediffuse aux OTA : une nuit repricée
//      ou rouverte peut être VENDUE dans les minutes qui suivent. Le retour en
//      arrière existe dans nos tables, pas chez le voyageur qui a réservé.
//      D'où `["sortant"]` sur tout ce qui touche l'API Beds24 en écriture, et
//      un `constatable` qui dit que ça part vers les plateformes — Dimitri doit
//      le lire AVANT le clic, pas le découvrir après.
//   2. UNE PUNAISE 📌 N'EST PAS UN PRIX. `pricing_set_manual` pose
//      `is_manual_override=true` : ni la relecture horaire de Beds24 ni
//      « appliquer le prix de base » ne repassent sur la date, et elle y reste.
//      `pricing_unlock` fait l'inverse : il rend la date à la relecture
//      horaire de Beds24 (lecture seule) et au prix de base — rien ne calcule
//      un prix aujourd'hui (cf. `MOTEUR_DE_PRIX_BRANCHE` ci-dessous). Les deux
//      sont marqués `["argent"]` pour cette raison : l'un fixe un montant,
//      l'autre décide QUI pourra l'écrire.
//
// ⚠️ RIEN NE CALCULE UN PRIX AUJOURD'HUI, ET LE REGISTRE NE DOIT PAS DIRE LE
// CONTRAIRE (Dimitri, 2026-09-16 : « ma question a démasqué une carte qui
// m'aurait menti »). Le cron horaire LIT Beds24 (lecture seule), PriceLabs n'a
// jamais été actif, et la prod ne porte aucune punaise. Six phrases de ce
// fichier parlaient d'un « pilotage automatique » qui « recalculerait » le prix
// d'une date déverrouillée : c'était faux, et une carte qui promet un moteur
// fait cliquer sur un filet qui n'existe pas. La sentinelle
// `src/lib/operateur-leviers-textes-vrais.test.js` refuse désormais ce
// vocabulaire tant que la constante ci-dessous est `false`.
//
// ⚠️ ET LE PLANCHER NE SE FORCE PAS DEPUIS UNE CONVERSATION. Le handler
// `pricing_set_manual` accepte `force:true + reason` pour passer sous
// `min_price` (garde 6ter, ADR-196). Ces deux clés sont VOLONTAIREMENT ABSENTES
// de `args_autorises` : la liste est fermée, donc une intention qui les porte
// est refusée ENTIÈREMENT. Le plancher reste bloquant pour tout ce que
// l'opérateur compose ; le forçage reste un geste de la main de Dimitri, dans
// l'écran, avec sa raison écrite par lui.
//
// ⚠️ CE MODULE NE SAIT PAS AGIR. Que des descriptions : c'est le navigateur de
// Dimitri, avec SON jeton, qui appelle l'action.
//
// ══ CE QUI N'EST PAS DÉCLARÉ, ET POURQUOI (le tri fait sur le CODE) ════════
//
// • pricing_block_release · beds24_reconciliation_resolve → INTERDITS ABSOLUS
//   (cf. lib/operateur-leviers.js). Les deux ROUVRENT la vente d'une date :
//   le dommage se produit chez un tiers, aucune confirmation ne le défait.
//
// • pricing_block_edit_dates → écarté par E1, POUR LA MÊME RAISON. Le nom ne
//   l'annonce pas, le code si : `pushBlockAvailability` fait un push
//   DIFFÉRENTIEL, et toute réduction de plage renvoie `numAvail=1` sur les
//   dates libérées. C'est `pricing_block_release` par un autre chemin —
//   interdire la porte et laisser la fenêtre est exactement le défaut qu'on
//   chasse ici. (Ses gardes fail-closed protègent le double-booking VU par
//   FBZ, pas la vente qui part ensuite.)
//
// • pricing_block_update_internal → ÉCARTÉ, ET LA RAISON A ÉTÉ RÉÉCRITE LE
//   2026-09-12 : l'ancienne était devenue FAUSSE, ce qui est pire qu'absente.
//   Elle disait « le socle refuse tout argument non scalaire, donc le levier
//   serait mort-né ». Le socle accepte désormais les objets BORNÉS
//   (`args_formes`, cf. `_forme.js`) — et `journey_composition_set`, écarté le
//   même jour pour le même motif, a depuis été déclaré. Cet argument-là est
//   mort ; trois autres, tirés du code, le remplacent :
//
//   1. LA LISTE DU SERVEUR EST UNE SEULE. `validateGuestFields`
//      (`lib/pricing-block.js`) énumère 13 clés dont `total_amount` — LE
//      MONTANT d'une résa directe. Le registre saurait en décrire une
//      sous-liste fermée sans lui, oui ; mais on écrirait alors une garde
//      MAISON sur le chemin argent, à côté de celle du serveur, et deux gardes
//      qui divergent finissent toujours par diverger du mauvais côté.
//   2. L'IDENTITÉ QU'IL RÉÉCRIT EST DÉJÀ FIGÉE AILLEURS. Un `pricing_block`
//      direct porte le voyageur d'un contrat SIGNÉ (PDF + sha256, ADR-184) et
//      d'un reçu. Renommer le voyageur après coup fait diverger la ligne du
//      document légal qui la porte — l'effet déborde le libellé, motif littéral
//      des INTERDITS_ABSOLUS.
//   3. EFFET DE BORD MESURÉ, invisible au libellé : poser N'IMPORTE QUEL champ
//      voyageur sur un bloc le fait sortir du périmètre d'auto-résolution
//      Beds24 (`hasAnyGuestField`, `lib/beds24-autoresolve.js`). Il va dans le
//      sens prudent — le bloc cesse d'être auto-rouvrable — mais une carte qui
//      annonce « corriger une adresse » ne dit pas ça.
//
//   ⚠️ DOUTE ASSUMÉ ET REMONTÉ : c'est le seul des écarts de cette famille qui
//   se discute. Si Dimitri le veut, il rentre avec une `args_formes` dont les
//   clés excluent `total_amount`, `guest_email` et `guest_phone` — mais c'est
//   un arbitrage CHEMIN ARGENT, donc le sien, pas celui de qui remplit le
//   registre.
//
// • pricing_logement_config → ÉCARTÉ, ET LA RAISON A ÉTÉ RÉÉCRITE LE
//   2026-09-12 : sa première moitié est morte, elle aussi. Elle disait « un
//   geste ne porte pas de verbe » — `_forme.js` porte `methode` depuis. Ce qui
//   reste, lu dans `handlePricingLogementConfigPatch` :
//
//   1. `methode` ne câble que POST et PATCH. Cette action en a QUATRE (GET lit,
//      POST crée + SEED depuis Beds24, PATCH bascule le mode, DELETE désactive)
//      — deux de ses faces resteraient hors d'atteinte, et un nom d'action qui
//      désigne quatre gestes ne se dit pas honnêtement sur une carte.
//   2. LE PATCH QUI COMPTE EST LA BASCULE DE MODE `manual ↔ pricelabs` :
//      il ne change pas un prix, il change QUI fixe les prix de ce logement. Le
//      résultat se joue chez PriceLabs et Beds24 (le handler va jusqu'à
//      interroger le marketplace Beds24 avant d'accepter) — il n'est pas
//      constatable chez nous le jour même. E2 tombe.
//   3. VERROU OPTIMISTE `If-Match` : le handler compare l'en-tête au
//      `updated_at` courant et, s'il est ABSENT, accepte quand même en loggant
//      « no_if_match_header ». Une intention composée ne porte pas d'en-tête :
//      chaque clic passerait donc par la porte sans verrou, et écraserait en
//      silence ce qu'un autre admin vient de régler.
//
// • pricing_sync_fetch_beds24 · beds24_reconciliation_auto_resolve → écartés :
//   ils sont dans `CRON_ACTIONS`, authentifiés par Bearer `CRON_SECRET`. Le
//   navigateur de Dimitri n'a que son JWT admin — la carte s'afficherait et le
//   clic répondrait 401. Un bouton mort qu'il découvrirait lui-même.
//
// • taxe_bareme_validate → écarté par E4. Le handler écrit `statut:'valide'` et
//   `validated_by = <Dimitri>` : le clic ne règle pas un paramètre, il ATTESTE
//   qu'un taux légal a été vérifié à la source officielle. Un taux que le
//   modèle aurait composé de mémoire serait signé du nom de Dimitri.
//   ⚠️ Doute assumé et remonté : si Dimitri veut ce geste, il rentre en vague 9
//   avec le taux repris de `taxe_bareme_detect` (source DELTA affichée), jamais
//   d'un chiffre inventé.

// ══ MOTEUR_DE_PRIX_BRANCHE — LE JOUR OÙ UN MOTEUR ÉCRIRA DES PRIX ═══════════
//
// `false` tant que rien ne calcule un prix : le cron horaire est en LECTURE
// SEULE (il recopie Beds24, il ne décide rien), PriceLabs n'a jamais été actif
// (`pricing_mode='manual'` sur 3/3 logements, aucune clé), et 0 punaise en prod.
//
// Passer à `true` LE JOUR où un moteur écrit des prix — PriceLabs branché :
// `pricing_mode='pricelabs'` sur au moins un logement ET clé active. Ce jour-là,
// relire les six phrases de ce fichier (`pricing_set_manual` constatable +
// sans_retour, `pricing_unlock` titre + constatable + ne_fait_pas +
// sans_retour, `pricing_apply_base` ne_fait_pas) : elles redeviennent vraies
// avec un moteur, et la sentinelle `operateur-leviers-textes-vrais.test.js`
// cesse de les interdire. Un booléen STRICT : la sentinelle refuse tout autre
// type, pour qu'un `"false"` ou un `undefined` ne lève pas la garde en silence.
const MOTEUR_DE_PRIX_BRANCHE = false;

// ══ « vise=logement » AU CATALOGUE — LE NOM DIT, PAS L'IDENTIFIANT ═════════
//
// Les leviers de cette famille qui portent `args_resolus` ne montrent plus
// `logement_id` au modèle : il pose « Cocon Naturel » dans `vise`, et le
// serveur retrouve `lc1` au moment de composer la carte
// (`lib/reperes-geste.js`). L'argument RESTE dans `args_autorises` — l'en
// retirer ferait refuser la carte au clic (`filtrerArgs`, rejoué par le
// navigateur) et casserait les aperçus qui le citent en `depend_de`.
//
// ⚠️ ET SI LE MODÈLE LE COMPOSE QUAND MÊME, LA CARTE NE VIENT PAS. On ne lui
// demande pas de s'abstenir : on rend la chose sans effet, et l'écran demande
// à Dimitri de nommer le logement. « Cocon » tout seul désigne DEUX logements
// aux commissions différentes — ce n'est pas un détail de confort.
const LEVIERS_PRICING = Object.freeze({
  // ── LA PHRASE DE DIMITRI ───────────────────────────────────────────────
  // « change le prix de Cocon Naturel à 40 € ». Le handler veut
  // logement_id + date_from + date_to + price_eur ; `raison` n'est qu'une
  // trace d'audit. Les dates sont obligatoires côté serveur (400
  // `date_invalide` sinon) : un prix sans période n'existe pas dans ce modèle,
  // c'est `pricing_set_base_price` qu'il faut alors.
  pricing_set_manual: {
    ecran: "logements",
    endpoint: "/api/beds24-mapping",
    action: "pricing_set_manual",
    // FERMÉE : ni `force` ni `reason` — le plancher min_price reste bloquant
    // pour tout ce que l'opérateur compose (cf. en-tête).
    // ⚠️ `min_stay` RETIRÉ LE 2026-09-16 (revue adverse, M3). Le handler le
    // sait toujours poser — mais AVEC un prix, donc avec une punaise 📌 : c'est
    // le geste de la main de Dimitri dans l'écran (le combo y reste, le HANDLER
    // est intouché). Pour l'opérateur, « deux nuits minimum du 20 au 31 » est
    // un AUTRE geste, `pricing_set_min_stay_period`, sans prix et sans punaise.
    // Laisser `min_stay` ici faisait router le séjour minimum vers le geste
    // n° 1 du chemin argent, avec le prix courant repoussé « pour rien » — et
    // punaisé.
    // ⚠️ ET C'EST LA SECONDE MOITIÉ DE LA CORRECTION #1440. La revue y avait
    // posé `args_indispensables: ["price_eur"]` pour que la carte cesse d'être
    // VERTE devant un 400 `prix_invalide`. La garde reste — elle vaut pour
    // toute composition sans prix — mais la phrase qui la déclenchait ne peut
    // plus arriver ici : elle a son propre levier. Les deux se complètent,
    // aucune ne remplace l'autre.
    args_autorises: [
      "logement_id",
      "date_from",
      "date_to",
      "price_eur",
      "raison",
    ],
    args_resolus: { logement_id: { objet: "logement", par: ["logement"] } },
    // ⚠️ LE PRIX EST OBLIGATOIRE CÔTÉ SERVEUR (400 `prix_invalide`, MPR3-2) :
    // « deux nuits minimum du 20 au 31 » composé SANS prix donnait une carte
    // verte vers un 400 (revue adverse #1440). Ce réglage ne sait pas poser
    // un séjour minimum seul — la carte le dit et grise.
    args_indispensables: ["price_eur"],
    titre: "Fixer le prix d'une période",
    constatable:
      "les dates prennent le prix demandé en € avec leur punaise 📌 — ni la relecture horaire de Beds24 ni « appliquer le prix de base » ne repasseront dessus — et le prix part vers Beds24, donc vers Airbnb et Booking",
    sujet: "calendrier",
    onglet: "info",
    cout: ["argent", "push_beds24"],
    ou_ca_part: ["beds24"],
    // ⚠️ LE GESTE N°1 DE LA MAQUETTE, ET LE PLUS INCOMPLET DU REGISTRE JUSQU'À
    // CE COMMIT : argent + sortant, en vague 4, sans une ligne sur ce qu'il
    // efface. `pricing_unlock`, son jumeau, en portait une depuis le 13/09.
    // La table PAR ARGUMENT du 2026-09-15 (prix · séjour minimum) n'a plus
    // d'objet depuis le 2026-09-16 : ce levier ne fait plus qu'UNE chose pour
    // l'opérateur (le prix), le séjour minimum a son propre geste. La mécanique
    // des tables par argument reste dans `lib/operateur-geste.js` et reste
    // testée — elle servira au prochain levier qui fera deux choses.
    sans_retour:
      "le prix d'avant n'est gardé nulle part : une fois posé, il ne revient pas — et enlever la punaise ne le restaure pas, le prix reste celui-là tant que personne ne le change",
    ne_fait_pas:
      "les réservations déjà encaissées ne bougent pas : seules les nuits encore à vendre prennent ce prix. Et il REFUSE un prix sous le plancher du logement — pour passer dessous, c'est l'écran des prix.",
    // ⚠️ L'APERÇU : la carte lit l'état de la période (prix groupés, punaises,
    // blocages) AVANT le clic. `valeur_arg` vise le prix, désormais TOUJOURS
    // présent ici (`args_indispensables`, et `min_stay` est parti) : la flèche
    // « 55 € → 75 € » a donc toujours un « après ».
    // ⚠️ `bornes: "inclusive"` : les nuits du 10 ET du 20 (`gte`/`lte` dans le
    // handler, `daily_pricing` = une ligne par nuit). Un blocage, lui, est
    // exclusif — voir `pricing_block_create`. Sans cette convention, la
    // lecture grisait un dos-à-dos légitime (revue adverse #1440).
    // ⚠️ `price_eur` DANS `depend_de` (revue adverse #1440, correction 6) :
    // la lecture le reçoit pour juger le PLANCHER avant le clic (« baisse le
    // Cocon à 30 € » sur un plancher à 34 € grisait vert, puis 400
    // below_floor). Lecture seule : rien n'est posé.
    apercu: {
      lecture: "pricing_periode_lire",
      depend_de: ["logement_id", "date_from", "date_to", "price_eur"],
      bornes: "inclusive",
      jeton: "x-apercu-version",
      champ: "prix",
      valeur_arg: "price_eur",
    },
    dit_comme: [
      "Baisse Cocon de 15 % ce week-end",
      "Mets le Nid à 40 € du 12 au 15",
      "Passe ce logement à 90 € pour les vacances",
    ],
    vague: 4,
    motif_requis: false,
    autonome: false,
    marquages: ["argent", "sortant"],
  },

  // Retire la punaise : la date redevient ÉCRIVABLE par la relecture horaire
  // de Beds24 (lecture seule — le cron LIT, il ne décide rien) et par
  // « appliquer le prix de base ». Marqué `argent` parce que le geste décide
  // QUI pourra écrire le prix de cette date ensuite : un changement fait chez
  // Beds24, ou le prix de base si Dimitri l'applique, remplacera le prix posé
  // à la main — sans nouveau clic sur CETTE date. Zéro push (GET Beds24 +
  // upsert local seulement).
  pricing_unlock: {
    ecran: "logements",
    endpoint: "/api/beds24-mapping",
    action: "pricing_unlock",
    args_autorises: ["logement_id", "date_from", "date_to"],
    args_resolus: { logement_id: { objet: "logement", par: ["logement"] } },
    titre: "Enlever la punaise sur des dates",
    constatable:
      "la punaise 📌 disparaît des dates ; le prix affiché ne bouge pas (c'est déjà celui de Beds24) — la relecture horaire de Beds24 et « appliquer le prix de base » pourront de nouveau l'écrire",
    sujet: "calendrier",
    onglet: "info",
    // Aucun push : GET Beds24 + upsert local. La lecture coûte du quota, pas
    // une écriture chez eux.
    cout: ["argent", "lecture_beds24"],
    ou_ca_part: ["personne"],
    ne_fait_pas:
      "il ne change aucun prix, n'en envoie aucun et n'en remet aucun : rien ne calcule vos prix aujourd'hui. Sans punaise, une date prend simplement ce que Beds24 dit à la prochaine relecture, ou le prix de base si vous l'appliquez",
    // Rien de neuf à annoncer : ce geste RETIRE. La carte montre donc l'état
    // (combien de nuits portent une punaise), sans flèche — il n'y a pas de
    // « après » connu : le prix ne bouge pas au clic, et ce qui l'écrira
    // ensuite (relecture Beds24, prix de base) n'est pas encore décidé.
    apercu: {
      lecture: "pricing_periode_lire",
      depend_de: ["logement_id", "date_from", "date_to"],
      bornes: "inclusive",
      jeton: "x-apercu-version",
      champ: "verrou",
    },
    dit_comme: [
      "Enlève les prix que j'ai fixés à la main sur ces dates",
      "Enlève la punaise sur ces dates",
      "Laisse Beds24 reprendre la main sur cette semaine",
    ],
    vague: 4,
    motif_requis: false,
    autonome: false,
    marquages: ["argent"],
    // ⚠️ LE FILET N'EXISTE PAS, ET LA CARTE DOIT LE DIRE (Dimitri, 2026-09-13).
    // Sa maquette annonçait « Annulable tant que personne n'a réservé » sous ce
    // geste. Faux : le prix d'avant n'est stocké nulle part. Et rien ne le
    // « recalcule » non plus (2026-09-16) : au clic, le prix ne bouge pas ; il
    // sera simplement remplacé par ce que Beds24 dit ensuite, ou par le prix de
    // base si Dimitri l'applique — sans que l'ancien soit gardé.
    sans_retour:
      "rien ne se perd au clic ; mais dès que la punaise est levée, un changement fait chez Beds24 ou « appliquer le prix de base » remplacera ce prix, et l'ancien n'est gardé nulle part",
  },

  // Prix de base du logement : DB SEULE (logement_pricing_config.base_price),
  // aucun push. Rien ne bouge au calendrier tant que `pricing_apply_base` n'a
  // pas été passé — c'est pour ça que le `constatable` parle de la fiche et
  // pas des dates : promettre le calendrier ici serait faux.
  pricing_set_base_price: {
    ecran: "logements",
    endpoint: "/api/beds24-mapping",
    action: "pricing_set_base_price",
    args_autorises: ["logement_id", "base_price"],
    args_resolus: { logement_id: { objet: "logement", par: ["logement"] } },
    titre: "Enregistrer le prix de base",
    constatable:
      "le prix de base en € change dans la carte tarification de la fiche ; le calendrier, lui, ne bouge pas encore",
    sujet: "calendrier",
    onglet: "info",
    cout: ["argent"],
    ou_ca_part: ["personne"],
    sans_retour: null, // un scalaire en base, reposable à l'identique
    // ⚠️ LA CONFUSION EST DANS LE LIBELLÉ MÊME : « enregistrer le prix de
    // base » ressemble à « poser ce prix ». Il ne pose rien.
    ne_fait_pas:
      "il n'applique ce prix à AUCUNE date et n'envoie rien à Beds24 : le calendrier ne bouge pas tant que « Appliquer le prix de base » n'a pas été passé",
    // ⚠️ L'APERÇU (2026-09-15) : la carte lit `pricing_config_lire` avec le
    // jeton de Dimitri et affiche « prix de base : 55 € → 75 € » — ou
    // « aucun prix de base enregistré → 75 € » quand il n'y en a pas encore,
    // jamais « 0 € → 75 € ». La lecture porte aussi le refus du mode non
    // manuel : le bouton est gris AVANT le clic, pas après.
    apercu: {
      lecture: "pricing_config_lire",
      depend_de: ["logement_id"],
      jeton: "x-apercu-version",
      champ: "base_price",
      valeur_arg: "base_price",
    },
    dit_comme: [
      "Le prix normal de ce logement, c'est 75 €",
      "Change le tarif habituel du Cocon",
      "Note que ce logement vaut 80 € la nuit",
    ],
    vague: 4,
    motif_requis: false,
    autonome: false,
    marquages: ["argent"],
  },

  // Applique le prix de base sur une plage : push price1 SANS punaise (les
  // dates restent écrivables par la relecture Beds24) et n'écrase JAMAIS une
  // date punaisée. ⚠️ ET PAR DÉFAUT IL N'ÉCRASE RIEN DU TOUT (vérifié dans
  // `handlePricingApplyBase`, 2026-09-16 : `const overwrite =
  // overwrite_existing === true` puis `if (overwrite || !hasPrice)`) :
  // `overwrite_existing` vaut `false` si l'écran ne le pose pas, et le handler
  // ne cible alors que les dates SANS prix (`price1` nul ou ligne absente) —
  // une date qui a déjà un prix, punaisée ou non, garde le sien. Les textes
  // ci-dessous le disent. Sans
  // `confirmStage`, le handler rend un RÉCAP et ne pousse rien — d'où
  // `confirmStage` dans la liste : c'est le seul moyen que la carte de Dimitri
  // aille au bout.
  pricing_apply_base: {
    ecran: "logements",
    endpoint: "/api/beds24-mapping",
    action: "pricing_apply_base",
    args_autorises: [
      "logement_id",
      "date_from",
      "date_to",
      "base_price",
      "overwrite_existing",
      "confirmStage",
    ],
    args_resolus: { logement_id: { objet: "logement", par: ["logement"] } },
    titre: "Appliquer le prix de base sur une période",
    constatable:
      "les dates de la période qui n'ont pas encore de prix prennent le prix de base en € et partent vers Beds24 ; les dates déjà tarifées et les 📌 restent intactes",
    sujet: "calendrier",
    onglet: "info",
    cout: ["argent", "push_beds24"],
    ou_ca_part: ["beds24"],
    sans_retour:
      "les prix écrasés sur la période ne sont gardés nulle part : ils ne reviennent pas, et ce qui est parti chez Beds24 est parti",
    ne_fait_pas:
      "il n'écrase JAMAIS une date punaisée 📌 et ne pose aucune punaise : il ne pose le prix de base que sur les dates sans prix ; une date qui a déjà un prix, punaisée ou non, garde le sien — sauf si l'écran demande explicitement d'écraser",
    apercu: {
      lecture: "pricing_periode_lire",
      depend_de: ["logement_id", "date_from", "date_to"],
      bornes: "inclusive",
      jeton: "x-apercu-version",
      champ: "prix",
      valeur_arg: "base_price",
    },
    dit_comme: [
      "Remets le tarif habituel sur tout le mois de mars",
      "Applique le prix normal sur cette période",
      "Repasse ces dates au prix de base du logement",
    ],
    vague: 4,
    motif_requis: false,
    autonome: false,
    marquages: ["argent", "sortant"],
  },

  // Le plancher. DB seule, zéro Beds24 — mais c'est lui qui REFUSE ensuite les
  // saisies manuelles trop basses (garde 6ter). Le mal-régler ne casse rien
  // bruyamment : ça bloque des ventes, ou ça n'en bloque plus. D'où `argent`.
  pricing_set_min_price: {
    ecran: "logements",
    endpoint: "/api/beds24-mapping",
    action: "pricing_set_min_price",
    args_autorises: ["logement_id", "min_price"],
    args_resolus: { logement_id: { objet: "logement", par: ["logement"] } },
    titre: "Régler le prix plancher d'un logement",
    constatable:
      "le prix plancher en € change dans la fiche ; toute saisie manuelle en dessous sera refusée ensuite",
    sujet: "calendrier",
    onglet: "info",
    cout: ["argent"],
    ou_ca_part: ["personne"],
    sans_retour:
      "l'ancien plancher n'est gardé nulle part et rien n'en garde la trace : pour le rendre, il faudra se souvenir du chiffre",
    ne_fait_pas:
      "il ne change aucun prix déjà posé, et ne remonte aucune date qui serait sous le plancher : il ne fait que refuser les saisies manuelles SUIVANTES",
    // Un plancher se juge contre celui d'aujourd'hui : la carte le montre.
    apercu: {
      lecture: "pricing_config_lire",
      depend_de: ["logement_id"],
      jeton: "x-apercu-version",
      champ: "min_price",
      valeur_arg: "min_price",
    },
    dit_comme: [
      "Je ne veux jamais descendre sous 50 € sur ce logement",
      "Mets un plancher à 60 € pour le Nid",
      "Interdis-moi de brader sous 45 €",
    ],
    vague: 4,
    motif_requis: false,
    autonome: false,
    marquages: ["argent"],
  },

  // Séjour minimum par défaut (room.minStay Beds24). Pas un montant, mais un
  // push RÉEL : trop haut, plus personne ne peut réserver, et ça part chez les
  // OTA tout de suite.
  pricing_set_min_stay_default: {
    ecran: "logements",
    endpoint: "/api/beds24-mapping",
    action: "pricing_set_min_stay_default",
    args_autorises: ["logement_id", "min_stay"],
    args_resolus: { logement_id: { objet: "logement", par: ["logement"] } },
    titre: "Régler le séjour minimum par défaut",
    constatable:
      "le nombre de nuits minimum change dans la fiche et part vers Beds24, donc vers les plateformes",
    sujet: "calendrier",
    onglet: "info",
    cout: ["push_beds24"],
    ou_ca_part: ["beds24"],
    sans_retour:
      "ce qui part chez Beds24 est parti : les plateformes ont déjà le nouveau minimum, et l'ancien n'est gardé nulle part",
    ne_fait_pas:
      "il ne touche pas le séjour minimum posé date par date sur une période (pour ça, c'est « fixer le séjour minimum d'une période ») : c'est le DÉFAUT de la chambre chez Beds24, et les réservations déjà prises ne bougent pas",
    // La carte montre le minimum d'aujourd'hui (notre copie de celui de
    // Beds24) avant d'en envoyer un autre — et dit non tout de suite si le
    // logement n'est rattaché à aucune chambre là-bas.
    apercu: {
      lecture: "pricing_config_lire",
      depend_de: ["logement_id"],
      jeton: "x-apercu-version",
      champ: "min_stay_default",
      valeur_arg: "min_stay",
    },
    // ⚠️ « SUR CE LOGEMENT » / « PAR DÉFAUT », JAMAIS DES DATES (2026-09-16,
    // M3) : c'est ce qui distingue ce geste de `pricing_set_min_stay_period`
    // dans la bouche de Dimitri. Une phrase avec « du X au Y » ici ferait
    // router une période vers le défaut de la chambre.
    dit_comme: [
      "Deux nuits minimum sur ce logement, par défaut",
      "Je ne veux plus de nuit seule au Cocon, en règle générale",
      "Passe le séjour minimum par défaut à trois nuits",
    ],
    vague: 4,
    motif_requis: false,
    autonome: false,
    marquages: ["sortant"],
  },

  // ══ SÉJOUR MINIMUM D'UNE PÉRIODE — L'ACTION NEUVE DU 2026-09-16 ═════════
  //
  // Décision Dimitri : « séjour minimum = action neuve, JAMAIS de punaise ».
  // Le handler `handlePricingSetMinStayPeriod` (lib/pricing-handlers.js,
  // « 6quater ») pousse `minStay` SEUL vers le calendar Beds24 — ni price1
  // ni numAvail —, attend le verdict, puis écrit `daily_pricing.min_stay` par
  // un UPDATE partiel : aucune source, aucun prix, aucune punaise. Une date
  // punaisée 📌 PASSE (le verrou protège le prix, rien d'autre — 2026-08-06)
  // et garde son prix.
  //
  // ⚠️ MARQUÉ « SORTANT » SEUL, PAS « ARGENT » — comme son voisin
  // `pricing_set_min_stay_default`, de même nature. Le registre réserve
  // « argent » aux gestes qui fixent un montant ou décident qui le fixera ;
  // celui-ci bloque des ventes courtes, ce qui se dit dans `ne_fait_pas` et
  // `constatable`, pas dans un marquage qui exigerait un montant avant/après
  // que rien ne peut afficher.
  //
  // ⚠️ « ENLÈVE LE SÉJOUR MINIMUM » ⇒ `min_stay = 1`, JAMAIS `null`. Beds24 ne
  // « retire » pas un override par absence : il le PRÉSERVE (smoke S-1,
  // #167b). Le seul moyen de rouvrir les nuits seules est d'envoyer 1 — et le
  // handler refuse `null` (400 `min_stay_invalid`), pour que l'absence ne
  // puisse jamais se lire comme un retrait.
  //
  // ⚠️ GARDE C2 (revue adverse) : une période qui chevauche un blocage actif
  // est REFUSÉE (409) — aucune preuve live qu'un push minStay seul préserve
  // `numAvail`, et sur une nuit fermée le contraire serait
  // `pricing_block_release` (interdit absolu) par la fenêtre. L'aperçu montre
  // les blocages de la période avant le clic, et grise.
  //
  // ⚠️ ET LE MIROIR BEDS24 DÉCIDE EN DEUX TEMPS (deux décisions Dimitri,
  // contre-revue de #1452) : une nuit JAMAIS LUE refuse toute la période (409
  // `miroir_incomplet` — le logement neuf avant sa première synchro), une nuit
  // FERMÉE se retire simplement de l'envoi. « Pré-régler une saison est un
  // usage normal » : à partir de 2027-05, 100 % des nuits sont fermées en prod
  // parce que la fenêtre de vente n'est pas ouverte — refuser là était rendre
  // le levier muet. Le geste pousse les segments ouverts et DIT ce qu'il a
  // fait : « posé sur 12 nuits, 48 ignorées parce que fermées ».
  //
  // ⚠️ PAS DE FILE DE REJEU (Q1, Dimitri) : Beds24 injoignable ⇒ refus franc,
  // Dimitri le redit ; un rejeu tardif écraserait un geste plus récent.
  pricing_set_min_stay_period: {
    // `calendrier`, pas `logements` : l'effet se voit sur le CALENDRIER,
    // comme `pricing_block_create` (leçon du 2026-09-09).
    ecran: "calendrier",
    endpoint: "/api/beds24-mapping",
    action: "pricing_set_min_stay_period",
    args_autorises: ["logement_id", "date_from", "date_to", "min_stay"],
    args_resolus: { logement_id: { objet: "logement", par: ["logement"] } },
    // ⚠️ SANS NOMBRE DE NUITS, CE GESTE N'EXISTE PAS (garde de #1440,
    // appliquée ici dès le premier jour) : le handler répond 400
    // `min_stay_invalid`, la carte doit le dire AVANT le clic.
    args_indispensables: ["min_stay"],
    // ⚠️ « ENLÈVE LE SÉJOUR MINIMUM » : LA PHRASE ÉTAIT AU CATALOGUE, LA VALEUR
    // QU'ELLE EXIGE N'Y ÉTAIT PAS (revue adverse 2026-09-16, MAJEUR 1). Le
    // modèle ne reçoit du levier que sa signature et son titre :
    // `min_stay` sortait NU, sans borne ni valeur, et rien ne lui disait
    // qu'« enlever » se compose `min_stay: 1` — le handler, lui, refuse `null`
    // et `0` par 400. La consigne n'existait que dans un COMMENTAIRE de code,
    // que le modèle ne voit jamais, et les tests passaient parce qu'ils
    // composaient la valeur à la main (leçon « tester sur les phrases
    // réelles »). Elle est donc dans le TITRE, qui, lui, part au catalogue.
    // ⚠️ Pas d'`args_valeurs` : il faudrait énumérer 1→30, soit ~80 caractères
    // de catalogue pour une borne que le handler porte déjà.
    titre: "Fixer le séjour minimum d'une période",
    constatable:
      "les nuits ENCORE OUVERTES de la période portent le nouveau nombre de nuits minimum au calendrier, sans punaise 📌 et sans changer leur prix, et il part vers Beds24, donc vers Airbnb et Booking ; il vous dit combien de nuits il a posées et combien il a ignorées parce que fermées",
    sujet: "calendrier",
    cout: ["push_beds24"],
    ou_ca_part: ["beds24"],
    // ⚠️ IL DISAIT « L'ANCIEN N'EST GARDÉ NULLE PART », ET C'ÉTAIT UNE PERTE
    // SANS NÉCESSITÉ (revue adverse, CRITIQUE 2) : le pré-SELECT de la garde
    // 9bis lit déjà les `min_stay` d'avant, l'audit `push_beds24_manual` les
    // porte maintenant nuit par nuit (`min_stay_avant`). Ce qui reste vrai —
    // et c'est cela qu'il faut dire — c'est que le geste ne se défait pas tout
    // seul : les plateformes ont déjà le nouveau minimum.
    sans_retour:
      "ce qui part chez Beds24 est parti : les plateformes ont déjà le nouveau minimum — l'ancien est gardé dans le journal, mais le remettre est un autre geste",
    // ⚠️ M4 (revue adverse) : le site direct n'a AUCUNE garde de séjour
    // minimum (api/sites.js lit `min_stay` et le rend, il ne refuse rien) —
    // ce geste ne bloque donc pas les séjours courts là-bas. Le dire, sinon la
    // carte promet un blocage qui n'existe pas sur un des trois canaux.
    ne_fait_pas:
      "il ne touche ni au prix, ni aux punaises, ni au défaut de la chambre, ni aux réservations prises ; il saute les nuits déjà fermées chez Beds24, et refuse tout s'il n'a jamais lu une de ces nuits ; sur votre site direct, aucune garde n'existe",
    // La carte lit la période : « 1 nuit minimum sur les 12 nuits → 2 », avec
    // les punaises et les blocages à côté (un blocage ⇒ le serveur refuse).
    // ⚠️ `bornes: "inclusive"` — OBLIGATOIRE depuis #1440 : sans lui,
    // `pricing_periode_lire` répond 400 `bornes_requises` et `_forme.js` refuse
    // le levier au chargement. Et c'est la bonne convention : le handler lit
    // `gte(date_from)` / `lte(date_to)`, les nuits du 20 ET du 31 sont dedans.
    // Un BLOCAGE, lui, est exclusif — ne pas recopier d'ici vers là-bas.
    apercu: {
      lecture: "pricing_periode_lire",
      depend_de: ["logement_id", "date_from", "date_to"],
      bornes: "inclusive",
      jeton: "x-apercu-version",
      champ: "sejour_min",
      valeur_arg: "min_stay",
    },
    // Des DATES, toujours : « du X au Y », « pour les vacances », « ce
    // week-end » — c'est ce qui le distingue du défaut de la chambre.
    dit_comme: [
      "Deux nuits minimum du 20 au 31 janvier",
      "Mets le Cocon à 3 nuits mini pour les vacances",
      "Enlève le séjour minimum sur ce week-end",
    ],
    vague: 4,
    motif_requis: false,
    autonome: false,
    marquages: ["sortant"],
  },

  // READ-and-CACHE : lit le minStay chez Beds24 et rafraîchit notre copie.
  // Le commentaire du handler est explicite — « ne touche JAMAIS Beds24 en
  // écriture ». Rien ne sort, rien ne coûte : vague 1.
  pricing_sync_min_stay_default_from_beds24: {
    ecran: "logements",
    endpoint: "/api/beds24-mapping",
    action: "pricing_sync_min_stay_default_from_beds24",
    args_autorises: ["logement_id"],
    args_resolus: { logement_id: { objet: "logement", par: ["logement"] } },
    titre: "Relire le séjour minimum chez Beds24",
    constatable:
      "le séjour minimum affiché dans la fiche se réaligne sur ce que dit Beds24, avec l'heure de la relecture",
    sujet: "calendrier",
    onglet: "info",
    cout: ["lecture_beds24"],
    ou_ca_part: ["personne"],
    sans_retour: null, // notre copie se réaligne ; Beds24 fait foi
    ne_fait_pas:
      "il n'écrit RIEN chez Beds24 : il recopie chez nous ce qu'ils disent, et écrase donc notre valeur locale si elle avait dérivé",
    // La carte montre NOTRE valeur d'aujourd'hui ; celle de Beds24, on ne la
    // connaît qu'après le clic — d'où l'état sans flèche.
    apercu: {
      lecture: "pricing_config_lire",
      depend_de: ["logement_id"],
      jeton: "x-apercu-version",
      champ: "min_stay_default",
    },
    dit_comme: [
      "Regarde combien de nuits minimum Beds24 a pour ce logement",
      "Relis chez Beds24 le nombre de nuits minimum de ce logement",
      "Est-ce que mon minimum de nuits est bien le même des deux côtés ?",
    ],
    vague: 1,
    motif_requis: false,
    autonome: false,
  },

  // Relecture complète du calendrier (540 j) depuis Beds24 vers daily_pricing.
  // GET seul : aucun push. Écrit des prix, mais n'en CHOISIT aucun — il recopie
  // la vérité Beds24, donc pas de marquage `argent` : il n'y aurait aucun
  // montant à afficher sur la carte, seulement du bruit.
  // Rate-limité 5 min par logement côté serveur (429).
  // ⚠️ `pricing_force_resync` — RECLASSÉ EN VAGUE 4 AVEC MARQUAGE `argent`
  // (2026-09-09, à l'ouverture de la vague 1, vérifié sur le TRIGGER en base).
  //
  // Il était en vague 1 (« propre et réversible ») au motif qu'il ne CHOISIT
  // aucun prix — il relit ceux de Beds24. C'est vrai, et c'est insuffisant :
  // `enforce_pricing_mode_write_protection` neutralise `cron_beds24_read` et
  // refuse `base_price_apply` sur une date verrouillée 📌, mais la source
  // `manual_admin_force_resync` est explicitement WHITELISTÉE et n'est
  // neutralisée par rien. Ce geste écrase donc les prix fixés à la main, et le
  // prix d'origine n'est nulle part : ce n'est pas réversible.
  //
  // Il n'est pas écarté pour autant — c'est le bouton que Dimitri a déjà, et
  // sa règle est « il propose, je clique ». Mais il doit LIRE ce qu'il perd :
  // le `constatable` le dit maintenant, et le marquage l'affiche en tête de
  // carte.

  // ⚠️ `pricing_force_resync` A ÉTÉ RETIRÉ LE 2026-09-09 — il est désormais un INTERDIT ABSOLU
  // (`lib/operateur-leviers.js`), sur décision de Dimitri en préparant la
  // vague 4. La raison voyage avec la règle, là-bas : une interdiction sans son
  // motif finit par être levée par quelqu'un qui la croit prudente à l'excès.

  // Vide la file des pushs en attente (échecs réseau ré-essayés). Ce n'est pas
  // du rangement : ce qui dort dans `pricing_outbox` sont des PRIX, et le drain
  // les envoie pour de bon. Rate-limité 60 s par logement.
  pricing_outbox_drain: {
    ecran: "logements",
    endpoint: "/api/beds24-mapping",
    action: "pricing_outbox_drain",
    args_autorises: ["logement_id"],
    args_resolus: { logement_id: { objet: "logement", par: ["logement"] } },
    titre: "Envoyer les prix restés en attente",
    constatable:
      "la file d'attente du logement se vide et les prix en € qu'elle contenait partent vers Beds24, donc vers les plateformes",
    sujet: "calendrier",
    onglet: "info",
    cout: ["argent", "push_beds24"],
    ou_ca_part: ["beds24"],
    sans_retour:
      "ce qui part chez Beds24 est parti : la file se vide, et les prix qu'elle contenait sont rediffusés aux plateformes",
    ne_fait_pas:
      "ce n'est pas du rangement : il ne supprime pas la file, il l'ENVOIE. Et il ne change aucun prix — il envoie ceux qui y dormaient, tels qu'ils y ont été posés.",
    // Envoyer une file sans l'avoir vue est exactement ce que l'aperçu doit
    // empêcher : la carte dit combien d'envois dorment là, et depuis quand.
    // Une file vide grise le bouton — cliquer pour n'envoyer rien est un clic
    // pour rien.
    apercu: {
      lecture: "pricing_outbox_lire",
      depend_de: ["logement_id"],
      jeton: "x-apercu-version",
      champ: "file",
    },
    dit_comme: [
      "Il y a des prix qui ne sont jamais partis, envoie-les",
      "Renvoie ce qui est resté en attente sur ce logement",
      "Mes derniers prix ne sont pas arrivés chez Airbnb, débloque ça",
    ],
    vague: 4,
    motif_requis: false,
    autonome: false,
    marquages: ["argent", "sortant"],
  },

  // Bloque des dates : push `numAvail=0` — la vente FERME chez les OTA. Sens
  // prudent (on perd des nuits, on n'en survend pas), donc pas d'interdit —
  // mais `sortant` quand même, et Dimitri doit le lire avant.
  // ⚠️ L'ASYMÉTRIE RESTE, ELLE A SEULEMENT CHANGÉ DE FORME (2026-09-17).
  // `pricing_block_release` — libérer un identifiant sans aucune vérification —
  // demeure un INTERDIT ABSOLU. Ce qui existe désormais, c'est une AUTRE
  // action, `pricing_block_reopen`, qui retrouve le blocage par la période et
  // passe par les trois gardes d'ADR-256/257 avant tout envoi. Fermer reste
  // d'un clic ; rouvrir demande une confirmation, une relecture et l'accord de
  // Beds24 en direct. Ne pas « simplifier » l'un vers l'autre.
  // `guest_fields` est hors liste — un blocage proposé est un blocage NU,
  // jamais une résa directe avec un voyageur et un montant dedans.
  pricing_block_create: {
    // ⚠️ `calendrier`, PAS `logements` — corrigé le 2026-09-09 en cherchant les
    // « constatable » qui nomment un écran différent de celui déclaré. L'effet
    // de ce geste se voit sur le CALENDRIER : envoyer Dimitri sur la fiche du
    // logement lui aurait fait chercher au mauvais endroit ce qu'il vient
    // d'approuver — et une carte qui ment sur l'après perd sa raison d'être.
    ecran: "calendrier",
    endpoint: "/api/beds24-mapping",
    action: "pricing_block_create",
    args_autorises: ["logement_id", "start_date", "end_date", "reason"],
    args_resolus: { logement_id: { objet: "logement", par: ["logement"] } },
    titre: "Bloquer des dates à la vente",
    constatable:
      "les dates passent en bloqué au calendrier et la fermeture part vers Beds24, donc vers les plateformes (la réouverture, elle, se fait à la main)",
    sujet: "calendrier",
    cout: ["push_beds24"],
    ou_ca_part: ["beds24"],
    // ⚠️ L'ASYMÉTRIE EST LE GARDE-FOU : le geste inverse est un INTERDIT
    // ABSOLU. Ce qui se ferme d'un clic se rouvre à la main, dans l'écran.
    // ⚠️ LA PHRASE DISAIT « JE NE SAIS PAS ROUVRIR », ET CE N'EST PLUS VRAI
    // DEPUIS LE 2026-09-17 : « Remettre des dates bloquées en vente » existe.
    // Ce qui reste vrai — et c'est cela qu'il faut dire — c'est que la
    // fermeture part tout de suite chez les plateformes, et que la rouvrir est
    // un autre geste, sous trois vérifications.
    sans_retour:
      "la fermeture part tout de suite chez les plateformes. Les y remettre est un autre geste, qui demande une confirmation et l'accord de Beds24",
    ne_fait_pas:
      "il ne crée pas une réservation : c'est un blocage NU, sans voyageur ni montant — et il n'annule pas les réservations déjà prises sur ces dates",
    // Ce que la carte montre : les blocages DÉJÀ posés sur ces dates — c'est
    // ce qui fait refuser un chevauchement côté serveur.
    // ⚠️ `bornes: "exclusive"` : `end_date` est le JOUR DE DÉPART, libre
    // (Convention A, ADR-111). « Bloque du 10 au 20 » ferme les nuits du 10 au
    // 19 ; un voyageur qui arrive le 20, ou un blocage qui finit le 10, ne
    // chevauche PAS — la RPC les accepte, la carte ne doit pas griser.
    apercu: {
      lecture: "pricing_periode_lire",
      depend_de: ["logement_id", "start_date", "end_date"],
      bornes: "exclusive",
      jeton: "x-apercu-version",
      champ: "blocage",
    },
    dit_comme: [
      "Bloque le Cocon du 10 au 20, il y a des travaux",
      "Ferme ces dates à la vente",
      "Rends ce logement indisponible la semaine prochaine",
    ],
    vague: 4,
    motif_requis: true,
    // Le nom sous lequel CE handler attend le motif. Sans lui, celui que
    // Dimitri écrit part dans le vide (2026-09-09).
    motif_champ: "reason",
    autonome: false,
    marquages: ["sortant"],
  },

  // ══ REMETTRE DES DATES BLOQUÉES EN VENTE — LE GESTE DU 2026-09-17 ═══════
  //
  // Dimitri : « les travaux sont finis, rouvre ces dates ». Le geste inverse
  // de `pricing_block_create`, et le plus dangereux du calendrier : une nuit
  // rouverte par erreur se vend chez Airbnb dans la minute, et le dommage est
  // chez le voyageur, pas chez nous.
  //
  // ⚠️ CE N'EST PAS `pricing_block_release`, QUI RESTE UN INTERDIT ABSOLU.
  // Celui-là libère un identifiant SANS aucune vérification. Ici, le serveur
  // retrouve le blocage PAR LA PÉRIODE, refuse tout ce qui n'est pas net (une
  // réservation directe, un morceau de blocage, plusieurs blocages, aucun),
  // puis passe par les TROIS GARDES d'ADR-256/257 : une confirmation de la
  // main de Dimitri, une relecture des réservations juste avant l'envoi, et un
  // contrôle EN DIRECT chez Beds24 — si Beds24 ne répond pas, on refuse.
  // Dimitri, 2026-09-16 : « un bouton qui contourne trois vérifications, c'est
  // la porte de derrière. »
  //
  // ⚠️ `confirm` N'EST PAS DANS `args_autorises`, ET C'EST TOUT LE SUJET
  // (revue adverse, C3). Un argument déclaré est un argument que le MODÈLE
  // compose : il aurait écrit `confirm: true` lui-même, et la première garde
  // serait vidée de l'intérieur. Il est posé au CLIC, par la carte, là où
  // Dimitri tape son motif — d'où `confirmation_au_clic`. Et un levier qui
  // porte ce champ n'est JAMAIS proposable : une proposition n'a pas de
  // frappe humaine derrière elle.
  //
  // ⚠️ IL NE SIMULE PAS, ET IL N'ENFILE RIEN. En simulation, le blocage
  // passerait « rouvert » chez nous pendant que Beds24 le garde fermé. Et un
  // rejeu différé rejouerait la réouverture des heures plus tard, sans
  // confirmation, sans relecture, sans contrôle : les trois gardes contournées
  // par la file. Beds24 injoignable ⇒ refus franc, Dimitri le redit.
  //
  // ⚠️ AUCUN EURO SUR CETTE CARTE, et c'est dit plutôt que laissé croire : le
  // marquage « argent » y est parce que des nuits repartent à la vente, pas
  // parce qu'un montant change. L'aperçu montre le BLOCAGE visé (dates, motif,
  // occupant noté, date de pose), jamais un prix.
  pricing_block_reopen: {
    ecran: "calendrier",
    endpoint: "/api/beds24-mapping",
    action: "pricing_block_reopen",
    // `reason` EST un argument (le serveur l'exige, 400 `reason_required`) —
    // mais il n'est jamais écrit par le modèle : `lib/geste-motif.js` le fige
    // sur des faits, ou Dimitri le tape. `confirm`, lui, N'EN EST PAS UN.
    args_autorises: ["logement_id", "start_date", "end_date", "reason"],
    args_resolus: { logement_id: { objet: "logement", par: ["logement"] } },
    args_indispensables: ["start_date", "end_date"],
    // La confirmation se pose au clic, jamais dans les arguments. Ce champ
    // rend aussi le levier NON PROPOSABLE (lib/operateur-intention.js).
    // ⚠️ NIVEAU DÉCIDÉ PAR DIMITRI LE 2026-10-01 : « remettre des nuits en
    // vente = VALIDATION À LA MAIN, jamais à la voix seule ». C'est CE champ
    // qui le porte — case cochée sur la carte, geste jamais proposé — avec les
    // marquages `argent`/`sortant` qui le font refuser à tout « oui » entendu
    // (`refuseALaVoix`, src/lib/carte-dite.js). Le retirer, ou retirer un de
    // ces deux marquages, fait retomber le geste en simple carte :
    // `src/lib/operateur-leviers-niveaux.test.js` rougit.
    confirmation_au_clic: true,
    // ⚠️ TITRE COURT À DESSEIN : le catalogue servi au modèle est à 8 % de son
    // plafond (`operateur-catalogue-marge`), et chaque geste neuf y pèse.
    // « Remettre en vente » dit la chose ; la période, elle, est dans les
    // arguments et sur la carte.
    titre: "Remettre en vente",
    constatable:
      "le blocage disparaît du calendrier et la réouverture part vers Beds24, donc vers Airbnb et Booking — ces nuits repartent à la vente dans la minute, au prix qui y est déjà affiché",
    sujet: "calendrier",
    cout: ["argent", "push_beds24"],
    ou_ca_part: ["beds24"],
    sans_retour:
      "une nuit remise en vente peut être vendue dans la minute : rebloquer ensuite ne rattrape pas une vente déjà faite ailleurs",
    ne_fait_pas:
      "il ne libère jamais un morceau de blocage, ne touche pas à une réservation directe (elle s'annule depuis la réservation), et ne change ni prix ni séjour minimum. Il refuse tant que Beds24 n'a pas confirmé qu'aucun séjour n'occupe ces nuits",
    // La carte montre CE QU'ON VISE : « du 12 au 15 octobre 2026, pour Mouad,
    // « travaux », posé il y a 4 jours ». Lecture dédiée — c'est la seule qui
    // rend un prénom, et elle porte sa règle écrite.
    // ⚠️ `bornes: "exclusive"` : `end_date` est le JOUR DE DÉPART, libre
    // (Convention A, ADR-111), comme pour `pricing_block_create`.
    apercu: {
      lecture: "pricing_blocage_lire",
      depend_de: ["logement_id", "start_date", "end_date"],
      bornes: "exclusive",
      jeton: "x-apercu-version",
      champ: "blocage",
    },
    dit_comme: [
      "Rouvre ces dates",
      "Débloque le Cocon du 10 au 20",
      "Les travaux sont finis, remets ces nuits en vente",
    ],
    vague: 4,
    motif_requis: true,
    // Le nom sous lequel CE handler attend le motif (400 `reason_required`).
    motif_champ: "reason",
    autonome: false,
    marquages: ["argent", "sortant"],
  },

  // Multiplicateur du canal direct (ADR-191) : le prix public direct = base ×
  // multiplicateur. Zéro Beds24, zéro push — mais c'est le prix que voient les
  // voyageurs du site. Scope serveur STRICT lc1/lc2/lc3 (400 hors scope).
  multiplier_set: {
    ecran: "logements",
    endpoint: "/api/beds24-mapping",
    action: "multiplier_set",
    args_autorises: ["logement_id", "channel", "multiplier", "active"],
    args_resolus: { logement_id: { objet: "logement", par: ["logement"] } },
    titre: "Régler le multiplicateur du canal direct",
    constatable:
      "la majoration change dans la fiche, et le prix en € que le voyageur voit sur votre site direct suit immédiatement",
    sujet: "calendrier",
    onglet: "info",
    cout: ["argent"],
    // Rien ne part : c'est notre page publique qui calcule le prix affiché à
    // la lecture. Mais un voyageur peut l'ouvrir dans la seconde.
    ou_ca_part: ["page_publique"],
    sans_retour:
      "l'ancienne majoration n'est gardée nulle part : pour revenir dessus, il faudra se souvenir du chiffre",
    ne_fait_pas:
      "il ne touche ni Beds24 ni les prix Airbnb/Booking, et ne change aucune réservation directe déjà payée : seul le prix affiché sur le site direct suit",
    // Un multiplicateur se juge contre celui d'aujourd'hui, jamais dans
    // l'absolu — et la carte le dit en pourcentage, pas en facteur.
    apercu: {
      lecture: "multiplier_lire",
      depend_de: ["logement_id"],
      jeton: "x-apercu-version",
      champ: "multiplier",
      valeur_arg: "multiplier",
    },
    dit_comme: [
      "Mets 15 % de plus sur mon site que sur Airbnb",
      "Change la majoration de mon site direct",
      "Aligne mon prix direct sur celui des plateformes",
    ],
    vague: 4,
    motif_requis: false,
    autonome: false,
    marquages: ["argent", "sortant"],
  },

  // Rappel annuel des barèmes de taxe de séjour : un email récapitulatif, vers
  // ADMIN_NOTIFICATION_EMAIL — c'est-à-dire vers Dimitri lui-même. Aucun
  // argument (le handler n'en lit aucun). Marqué `sortant` parce qu'un mail
  // part quand même d'ici : le destinataire ne change pas la nature du geste.
  taxe_reminder_send: {
    ecran: "logements",
    endpoint: "/api/beds24-mapping",
    action: "taxe_reminder_send",
    args_autorises: [],
    titre: "Envoyer le rappel des barèmes de taxe",
    constatable:
      "un envoi part vers la boîte mail de Dimitri avec le catalogue des communes, et la date de dernier rappel se met à jour dans la liste",
    sujet: "reglages",
    onglet: "info",
    cout: ["envoi"],
    // Le destinataire est Dimitri lui-même (ADMIN_NOTIFICATION_EMAIL). Ça ne
    // rend pas le geste inoffensif : un mail parti ne se rappelle pas, et la
    // date de dernier rappel est posée au passage.
    ou_ca_part: ["boite_mail"],
    sans_retour:
      "un mail parti ne se rappelle pas, et la date de dernier rappel est posée : le prochain envoi automatique compte à partir de maintenant",
    ne_fait_pas:
      "il ne change aucun barème et ne valide rien : c'est un récapitulatif à lire, pas une mise à jour des taux",
    // ⚠️ AUCUN ARGUMENT, DONC AUCUNE DÉPENDANCE — et ce n'est pas un aperçu
    // vide pour autant : ce qu'il y a à voir avant ce clic, c'est le CONTENU
    // qui va partir (combien de communes, combien non validées, depuis quand
    // le dernier rappel).
    apercu: {
      lecture: "taxe_bareme_lire",
      depend_de: [],
      jeton: "x-apercu-version",
      champ: "bareme",
    },
    dit_comme: [
      "Envoie-moi le récapitulatif des taxes de séjour",
      "Rappelle-moi les barèmes des communes",
      "Fais-moi le point sur la taxe de séjour",
    ],
    vague: 2,
    motif_requis: false,
    autonome: false,
    marquages: ["sortant"],
  },

  // ══ « VÉRIFIE BEDS24 » — UN LEVIER QUI NE FAIT QUE LIRE (2026-09-14) ══════
  //
  // Dimitri : « compare mes données avec Beds24 ». Le geste existe déjà et il
  // est ENTIÈREMENT en lecture : `beds24_reconciliation_status` compare le
  // miroir local `daily_pricing.num_avail` (alimenté par le webhook et le cron
  // 4 h, ADR-254) aux blocs et réservations de l'app, et rend les segments qui
  // divergent. ZÉRO écriture, ZÉRO appel sortant vers Beds24, ZÉRO verrou.
  //
  // ⚠️ NE PAS LE CONFONDRE AVEC SES DEUX VOISINS, ET LA DIFFÉRENCE EST TOUT :
  //  · `beds24_reconciliation_resolve` TRAITE une divergence — son mode
  //    « rouvrir » pousse `numAvail=1` chez Beds24 : INTERDIT ABSOLU, le
  //    dommage se produit chez un tiers et aucune confirmation ne le défait ;
  //  · `pricing_force_resync` ÉCRASE les prix posés à la main sur toute la
  //    période lue : INTERDIT ABSOLU aussi, « Dimitri le fera lui-même ».
  // Ce levier-ci n'est ni l'un ni l'autre : il REGARDE, et il s'arrête là.
  //
  // ⚠️ AUCUN ARGUMENT, ET C'EST LE HANDLER QUI LE DIT : il n'en lit aucun (ni
  // logement, ni fenêtre de dates). Le seuil « actionnable » vient de
  // l'environnement serveur (`BEDS24_RECON_MAX_ACTIONABLE_NIGHTS`, 60 jours par
  // défaut), l'horizon est fixe (540 jours), et le périmètre est l'ensemble des
  // logements actifs mappés. Déclarer un `logement_id` que personne ne lit
  // aurait donné une carte qui promet un filtre inexistant.
  //
  // ⚠️ ET LE VERBE : le routeur `api/beds24-mapping` ne contrôle pas la méthode
  // par action (il lit l'action dans le corps OU la requête) — le POST du
  // moteur de gestes atteint donc bien ce handler, que l'écran, lui, appelle en
  // GET. Rien à déclarer ici : le défaut POST convient.
  beds24_reconciliation_status: {
    // L'écran où Dimitri le CONSTATE : Paramètres › Sync Beds24, le seul
    // endroit où `src/tabs/Beds24SyncPanel.jsx` appelle cette action.
    ecran: "params",
    endpoint: "/api/beds24-mapping",
    action: "beds24_reconciliation_status",
    args_autorises: [],
    titre: "Comparer nos données avec celles de Beds24",
    // ⚠️ PHRASE CORRIGÉE LE 2026-09-14, ET LE DÉFAUT MÉRITE D'ÊTRE GARDÉ ICI.
    // Elle disait « la liste des écarts SE RAFRAÎCHIT dans Paramètres › Sync
    // Beds24 ». C'était FAUX : le clic ne touche aucun état de l'écran — le
    // panneau refait son propre appel, pour son compte, quand Dimitri l'ouvre.
    // Le geste rendait son résultat à `executerGeste`… qui ne l'affichait nulle
    // part. Un geste sans effet dont la phrase promet un effet est pire qu'un
    // geste absent : on clique, on ne voit rien, et on cherche ce qui a cassé.
    // Corrigé DES DEUX CÔTÉS : la carte affiche désormais le `resume` composé
    // par le serveur (convention `lib/geste-resume.js`), et la phrase dit ce
    // qui se passe vraiment — sur la CARTE, pas sur un écran qu'on n'a pas
    // ouvert.
    constatable:
      "la carte affiche, juste sous le bouton, ce qui ne colle pas entre votre calendrier et celui de Beds24 : combien de périodes sont bloquées chez Beds24 alors qu'elles sont ouvertes chez vous — soit une réservation qui n'est pas remontée chez vous, soit un blocage oublié, à vérifier AVANT de vendre ces nuits — et combien le sont dans l'autre sens : des nuits que vous croyez fermées et qui sont encore en vente chez Beds24, le cas grave. Plus le nombre de logements touchés. RIEN n'est modifié nulle part — c'est un constat. Le détail, logement par logement et date par date, se lit dans Paramètres › Sync Beds24.",
    // Vague 1 : la lecture est ce qu'il y a de plus réversible — il n'y a rien
    // à défaire. Elle n'écrit pas une ligne, n'appelle pas Beds24, et son
    // résultat s'affiche sur l'écran que le levier nomme.
    sujet: "reglages",
    cout: ["rien"],
    ou_ca_part: ["personne"],
    sans_retour: null, // une lecture ne se défait pas : il n'y a rien à défaire
    ne_fait_pas:
      "il ne RÉSOUT aucun écart, ni chez nous ni chez Beds24, et ne rafraîchit pas l'écran Sync : il rend un constat, et le traitement d'une divergence reste un autre geste",
    apercu_absent:
      "un aperçu montrerait l'état AVANT le clic — or ce geste EST cette lecture. Il n'y a rien à lire avant de lire.",
    dit_comme: [
      "Est-ce que mes calendriers sont à jour ?",
      "Est-ce que tout est synchro ?",
      "Vérifie Beds24",
    ],
    vague: 1,
    motif_requis: false,
    // ⚠️ `false` BIEN QUE LES QUATRE QUESTIONS DE L'AUTONOMIE SOIENT SATISFAITES
    // (rien à perdre, rien ne sort, constatable le jour même, zéro argent).
    // L'autonomie n'est pas une propriété qu'on déduit : c'est une liste que
    // Dimitri a arrêtée geste par geste, et celui-ci n'y a pas été soumis.
    // Le doute tranche vers `false` — le coût d'un « non » est un clic.
    autonome: false,
  },
});

// ══ LES BOOLÉENS DE CETTE FAMILLE, EN MOTS — `levier.argument` ════════════
//
// F5 (03/10/2026) : déplacées telles quelles de `lib/operateur-leviers.js`,
// qui les rassemble (la règle et ses gardes sont écrites là-bas, au-dessus de
// `NOMS_BOOLEENS`). Une phrase se déclare ICI, à côté de son levier, et nulle
// part ailleurs.
const BOOLEENS_LIBELLES = Object.freeze({
  "pricing_apply_base.overwrite_existing": {
    oui: "Écraser les prix déjà posés à la main",
    non: "Garder les prix déjà posés à la main",
  },
  "multiplier_set.active": {
    oui: "Activer le multiplicateur",
    non: "Couper le multiplicateur",
  },
});

module.exports = {
  LEVIERS_PRICING,
  MOTEUR_DE_PRIX_BRANCHE,
  BOOLEENS_LIBELLES,
};
